import { IBackupUser, IBackupUserServiceResponse } from 'legacy/lut/classes/models/IBackupUser'
import { TSPRSearchPayload, TSPRSearchResult } from 'legacy/lut/classes/models/ISPRSearch'
import { IAction } from 'legacy/lut/classes/models/onboarding/IAction'
import { IMainParam } from 'legacy/lut/classes/models/onboarding/IMainParam'
import { INotificationData } from 'legacy/lut/classes/models/onboarding/INotificationTemplate'
import { IUserAccessStatusToRegistration } from 'legacy/lut/classes/models/onboarding/IUserAccessStatusToRegistration'
import { RequestStatus, RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import { IReconfirmationDetails } from 'legacy/lut/components/widgets/public/onboarding/bpReconfirmation/detailedList/models/IReconfirmationDetails'
import { WARNING_MESSAGE } from 'legacy/lut/components/widgets/public/onboarding/bpRegistration/helpers/checkAccess'
import { IBPRegistrationCompanyData } from 'legacy/lut/components/widgets/public/onboarding/bpRegistration/models'
import { getErrorResponse } from 'legacy/lut/functions'
import { appGetState } from 'legacy/lut/store'

import { IPaging } from 'core-components/types'
import { IDataProviderFilter, IDataProviderSort, IFilter } from 'core-components/types'
import { getTokens } from 'core/classes/helpers/StorageHelper'
import WebSocketStore from 'core/classes/services/WebSocketStore'
import { TResponse, WebSocketClient } from 'core/classes/ws/WebSocketClient'
import Logger from 'core/utils/Logger'
import { IValidationParamsErrorPayload } from 'onboarding/types/request'

import { TAmsSearchPayload, TAmsSearchResult } from '../../models/IAmsSearch'

export type TRequestTriad = {
  requestId: number
  actionId: number
  transitionId: number
}

export type TExecuteTransitionPayload<T> = TRequestTriad & {
  onboardingRequestData?: T
  modifiedFields?: string[]
}

export enum EOnboardingEvent {
  importRequestStatus = 'import:request-status',
  importRequestBatchCreated = 'import:request-batch-created',
  importRequestProgress = 'import:request-progress',
  importAPLStatus = 'import:apl-status',
  importAPLAMSStatus = 'import:apl-ams-status',
  importAPLBatchCreated = 'import:apl-batch-created',
  importAPLAMSBatchCreated = 'import:apl-ams-batch-created',
  importAPLMAPStatus = 'import:apl-map-status',
  importAPLMAPBatchCreated = 'import:apl-map-batch-created',
  uploadNonEligiblePartnerList = 'upload:non-eligible-partner-list',
  requestRejectedActionExpired = 'request-rejected:action-expired',
}

export interface ILastPartnerChange {
  oldPartnerId: string
  newPartnerId: string
  changedBy: string
  changedAt: string
}

export interface IPeriod {
  startDate: string
  endDate: string
}

class OnboardingApi {
  public async subscribe(eventName: EOnboardingEvent) {
    const wsClient = WebSocketStore.get('onboarding')

    await wsClient.call({
      service: 'proxy-subscription',
      action: 'subscribe',
      payload: {
        subscribe: eventName,
      },
    })

    return async () => {
      return wsClient.call({
        service: 'proxy-subscription',
        action: 'unsubscribe',
        payload: {
          subscribe: eventName,
        },
      })
    }
  }

  public async subscribeAndListen<T>(
    eventName: EOnboardingEvent,
    observer: (payload: T) => any,
    shouldSubscribe?: boolean
  ) {
    const wsClient = WebSocketStore.get('onboarding')

    let unsubscribe: (() => Promise<TResponse<Record<string, any>>>) | null = null
    if (shouldSubscribe) {
      unsubscribe = await this.subscribe(eventName)
    }

    wsClient.on(eventName, observer)

    return async () => {
      if (unsubscribe) {
        await unsubscribe()
      }

      wsClient.unsubscribe(eventName, observer)
    }
  }

  public async initWSClient(wsKey: string, wsUrl: string) {
    if (WebSocketStore.has(wsKey)) {
      return WebSocketStore.get(wsKey)
    } else {
      const client = new WebSocketClient()
      client.init(wsUrl).catch(Logger.error)
      WebSocketStore.add(client, wsKey)

      return client
    }
  }

  public authorizeByToken = async (wsKey: string, token: string) => {
    const wsClient = WebSocketStore.get(wsKey)

    const response = await wsClient.call({
      service: 'auth',
      action: 'token',
      payload: { token },
      returnError: true,
    })
    if (response.status === 'success') {
      /**
       * This is dirty hack. WebSocketClient have to be rewritten. It should not know anything about auth
       */
      // @ts-ignore
      wsClient.authSecured = true
    }
  }

  public executeTransition = async (payload: TRequestTriad) => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const [token] = await getTokens(appGetState().app.ownerId)
      const res = await wsClient.call({
        service: 'action',
        action: 'changeAction',
        payload,
        token: token || undefined,
        withoutTokenRefresh: true,
        isAuth: true,
      })
      return {
        success: true,
        message: res.data.message,
      }
    } catch (e: any) {
      return { success: false, message: e }
    }
  }

  public validateCompanyData = async (data: IBPRegistrationCompanyData) => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const [token] = getTokens(appGetState().app.ownerId)
      const response = await wsClient.call({
        service: 'action',
        action: 'validate-bp-partner-data',
        payload: data,
        token: token || undefined,
        withoutTokenRefresh: true,
        isAuth: true,
        returnError: true,
      })

      if (response.status === 'fail' && response.data instanceof Array) {
        return {
          success: false,
          errors: response.data,
        }
      }

      return {
        success: response.status === 'success',
        message: response.message || '',
      }
    } catch (e: any) {
      return {
        success: false,
        errors: e.message,
      }
    }
  }

  public submitBPStep = async <T>(
    payload: TExecuteTransitionPayload<T>
  ): Promise<{
    success: boolean
    message?: string
    errors?: IValidationParamsErrorPayload[] | string
    editableRequestData: T | null
    action: IAction | null
  }> => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const [token] = await getTokens(appGetState().app.ownerId)
      const res = await wsClient.call<{
        onboardingRequestData: T
        action: IAction
        error?: IValidationParamsErrorPayload[]
      }>({
        service: 'bp-registration',
        action: 'submit-step',
        payload: {
          ...payload,
          onboardingRequestData: payload.onboardingRequestData
            ? JSON.stringify(payload.onboardingRequestData)
            : undefined,
        },
        token: token || undefined,
        withoutTokenRefresh: true,
        isAuth: true,
        returnError: true,
      })

      if (res.status === 'fail') {
        return {
          success: false,
          errors: res.data.error,
          editableRequestData: null,
          action: null,
        }
      }

      return {
        success: res.status === 'success',
        message: res.message || '',
        editableRequestData: res.data.onboardingRequestData,
        action: res.data.action,
      }
    } catch (e: any) {
      return { success: false, errors: e.message, editableRequestData: null, action: null }
    }
  }

  public resendNotification = async (
    campaignId: number,
    notificationData?: Partial<INotificationData>
  ) => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const [token] = await getTokens(appGetState().app.ownerId)
      const res = await wsClient.call({
        service: 'notification',
        action: 'resend',
        payload: {
          campaignId,
          notificationData,
        },
        token: token || undefined,
        withoutTokenRefresh: true,
        isAuth: true,
      })
      return {
        success: true,
        message: res.data.message,
      }
    } catch (e: any) {
      return { success: false, message: e }
    }
  }

  public sendRejectNotification = async (
    requestId: number,
    templateId: number,
    mailTo: string,
    CC: string,
    mailFrom: string
  ) => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const [token] = await getTokens(appGetState().app.ownerId)
      const res = await wsClient.call({
        service: 'notification',
        action: 'reject',
        payload: {
          RequestId: requestId,
          params: {
            templateUUID: templateId,
            mailTo,
            CC,
            mailFrom,
          },
        },
        token: token || undefined,
        isAuth: true,
      })
      return {
        success: true,
        message: res.data.message,
      }
    } catch (e: any) {
      return { success: false, message: e }
    }
  }

  public executeService = async (actionId: number, serviceUrl: string) => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const [token] = await getTokens(appGetState().app.ownerId)
      const res = await wsClient.call({
        service: 'action',
        action: 'executeService',
        payload: {
          params: {
            actionId,
            serviceUrl,
          },
        },
        token: token || undefined,
        isAuth: true,
      })
      return {
        success: true,
        message: res.data.message,
      }
    } catch (e: any) {
      return { success: false, message: e }
    }
  }

  public rejectRequest = async (reqId: number, comment: string) => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const [token] = await getTokens(appGetState().app.ownerId)
      const res = await wsClient.call({
        service: 'request',
        action: 'rejectRequest',
        payload: {
          RequestId: reqId,
          params: {
            comment,
          },
        },
        token: token || undefined,
        isAuth: true,
      })
      return {
        success: true,
        message: res.data.message,
      }
    } catch (e: any) {
      return { success: false, message: e }
    }
  }
  public callRecalcAudience = async (reqId: number, comment: string) => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const [token] = await getTokens(appGetState().app.ownerId)
      const res = await wsClient.call({
        service: 'request',
        action: 'recalcWriteAudience',
        payload: {
          RequestId: reqId,
          params: {
            historyMessage: comment,
          },
        },
        token: token || undefined,
        isAuth: true,
      })
      return {
        success: true,
        message: res.data.message,
      }
    } catch (e: any) {
      return { success: false, message: e }
    }
  }

  /**
   * @deprecated use actionService.reopenOne instead
   * @param actionId
   * @param requestId
   */
  public reopenAction = async (
    actionId: number,
    requestId: number
  ): Promise<{ success: boolean; item?: IAction; message?: string }> => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const [token] = await getTokens(appGetState().app.ownerId)
      const res = await wsClient.call<IAction>({
        service: 'action',
        action: 'setToWIP',
        payload: {
          actionId,
          requestId,
        },
        token: token || undefined,
        withoutTokenRefresh: true,
        isAuth: true,
      })

      return {
        success: true,
        item: res.data,
      }
    } catch (e: any) {
      return { success: false, message: e }
    }
  }

  public fetchMainParameter = async (
    type: string,
    pid: string,
    page: number,
    size: number,
    contactId?: string,
    sort?: IDataProviderSort[]
  ): Promise<Required<TResponse<{ specializations: IMainParam[] }>>> => {
    const wsClient = WebSocketStore.get('onboarding')
    let response
    let message = 'Failed to fetch specialization'

    try {
      const [token] = getTokens(appGetState().app.ownerId)
      response = await wsClient.call<{ specializations: IMainParam[] }>({
        service: 'request',
        action: 'partner-specialization-selection',
        payload: {
          type,
          partnerId: pid,
          page,
          size,
          contactId,
          sort,
        },
        token: token || undefined,
        withoutTokenRefresh: true,
        isAuth: true,
      })
      if (response && response.status === 'success') {
        message = response.data && response.data.specializations ? '' : 'No required data returned'
      }
    } catch (e: any) {
      response = getErrorResponse(e || message, { specializations: [] })
    }

    return { ...response, message: response.message || message }
  }

  public verifyToken = async (token: string) => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const res = await wsClient.call({
        service: 'auth',
        action: 'verifyToken',
        payload: {
          token,
        },
        withoutTokenRefresh: true,
      })
      return {
        success: true,
        message: res.data.message,
      }
    } catch (e: any) {
      return { success: false, message: e }
    }
  }

  public fetchBPRegData = async (pid: string) => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const res = await wsClient.call({
        service: 'bp-registration',
        action: 'list-by-partner',
        payload: {
          partnerId: pid,
        },
        withoutTokenRefresh: true,
        isAuth: true,
      })
      return res
    } catch (e: any) {
      return { success: false, message: e, data: [] }
    }
  }

  public fetchDetailedBPReconfirmationList = async (
    partnerId: string,
    period: IPeriod
  ): Promise<Required<TResponse<{ result: IReconfirmationDetails[] }>>> => {
    const wsClient = WebSocketStore.get('onboarding')
    const payload = { partnerId, period }
    let message = 'Failed to fetchDetailedBPReconfirmationList'
    let response

    try {
      response = await wsClient.call<{ result: IReconfirmationDetails[] }>({
        service: 'bpReconfirmation',
        action: 'listByPartnerAndPeriod',
        payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })
      if (response.status === 'success') {
        message = ''
      }
    } catch (e: any) {
      response = getErrorResponse(e, {
        result: [],
      })
    }

    return { ...response, message: response.message ?? message }
  }

  public searchCompanies = async (payload: {
    country: string
    communicationName?: string
    vatId?: string
    city?: string
    page?: number
    itemsPerPage?: number
  }) => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const res = await wsClient.call({
        service: 'bp-registration',
        action: 'search-companies',
        payload: payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })
      return res
    } catch (e: any) {
      return { success: false, message: e, data: [] }
    }
  }

  public fetchPartnersForBPRegistrationStatusCheckNew = async (
    fields: Record<string, any>,
    pagination: IPaging
  ) => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const res = await wsClient.call({
        service: 'bp-registration',
        action: 'search',
        payload: {
          ...fields,
          page: pagination.page + 1,
          itemsPerPage: pagination.pageSize,
        },
        withoutTokenRefresh: true,
        isAuth: true,
      })
      return res
    } catch (e: any) {
      return { success: false, message: e, data: [] }
    }
  }

  public fetchBackupUsers = async (
    filter: IDataProviderFilter,
    pagination: IPaging,
    sort?: IDataProviderSort[]
  ) => {
    const wsClient = WebSocketStore.get('onboarding')
    let result
    try {
      result = await wsClient.call<IBackupUserServiceResponse>({
        service: 'user-backup',
        action: 'fetch-list',
        payload: {
          kendoConditions: filter,
          page: pagination.page + 1,
          size: pagination.pageSize,
          sort: sort,
        },
        withoutTokenRefresh: true,
        isAuth: true,
      })
    } catch (e: any) {
      result = getErrorResponse(e.message || 'Failed to fetchBackupUsers', { items: [] })
    }

    return result
  }

  public amsSearch = async (
    filter: IDataProviderFilter,
    pagination: IPaging,
    sort: IDataProviderSort[] = []
  ) => {
    const wsClient = WebSocketStore.get('onboarding')

    const fields = {}
    const filters = filter.filters as IFilter[]
    filters.forEach((filter) => {
      fields[filter.field] = filter.value
    })

    if (!fields['CompanyLegalName'] && !fields['LocationID'] && !fields['CompanyCountry']) {
      return { data: { items: [] } }
    }

    const filterData = {
      CompanyName: fields['CompanyLegalName'],
      LocationID: fields['LocationID'],
      Country: fields['CompanyCountry'],
    }

    const payload: TAmsSearchPayload = {
      companyName: filterData.CompanyName,
      country: filterData.Country,
      locationId: filterData.LocationID,
      page: pagination.page + 1,
      size: pagination.pageSize,
      skipTotal: false,
      sort,
    }

    try {
      const result = await wsClient.call<TAmsSearchResult>({
        service: 'apl',
        action: 'ams-search',
        payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })

      return result
    } catch (e: any) {
      return {
        success: false,
        message: e,
        data: { items: [] },
      }
    }
  }

  public simplePartnerSearch = async (
    filter: IDataProviderFilter,
    pagination: IPaging,
    sort: IDataProviderSort[] = []
  ) => {
    const wsClient = WebSocketStore.get('onboarding')

    const fields = {}
    const filters = filter.filters as IFilter[]
    filters.forEach((filter) => {
      fields[filter.field] = filter.value
    })

    if (!fields['CompanyLegalName'] && !fields['CompanyTaxID'] && !fields['CompanyCountry']) {
      return { data: { items: [] } }
    }

    const filterData = {
      CompanyName: fields['CompanyLegalName'],
      TaxID: fields['CompanyTaxID'],
      Country: fields['CompanyCountry'],
    }

    const payload: TSPRSearchPayload = {
      companyName: filterData.CompanyName,
      country: filterData.Country,
      taxId: filterData.TaxID,
      page: pagination.page + 1,
      size: pagination.pageSize,
      skipTotal: false,
      sort,
    }

    try {
      const result = await wsClient.call<TSPRSearchResult>({
        service: 'spr',
        action: 'search-eligible-to-buy',
        payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })

      return result
    } catch (e: any) {
      return {
        success: false,
        message: e,
        data: { items: [] },
      }
    }
  }

  public saveBackupUser = async (payload: Partial<IBackupUser>) => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const res = await wsClient.call({
        service: 'user-backup',
        action: 'save',
        payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })
      return { success: true, message: res.data.message }
    } catch (e: any) {
      return { success: false, message: e }
    }
  }

  public deleteBackupUser = async (id: number) => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const res = await wsClient.call({
        service: 'user-backup',
        action: 'delete',
        payload: { id },
        withoutTokenRefresh: true,
        isAuth: true,
      })
      return { success: true, message: res.data.message }
    } catch (e: any) {
      return { success: false, message: e }
    }
  }

  public createContactProfilingRequest = async (
    contactId: string,
    updatedFields: Record<string, any>
  ): Promise<{
    success: boolean
  }> => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const res = await wsClient.call({
        service: 'profiling-request',
        action: 'contact-request',
        payload: { contactId, updatedFields },
        withoutTokenRefresh: true,
        isAuth: true,
      })
      return { success: true }
    } catch (e: any) {
      return { success: false }
    }
  }

  public createPartnerProfilingRequest = async (
    partnerId: string,
    updatedFields: Record<string, any>
  ): Promise<{
    success: boolean
  }> => {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const res = await wsClient.call({
        service: 'profiling-request',
        action: 'partner-request',
        payload: { partnerId, updatedFields },
        withoutTokenRefresh: true,
        isAuth: true,
      })
      return { success: true }
    } catch (e: any) {
      return { success: false }
    }
  }

  public getAttachment = async (requestId: number, actionUUID: string, transitionName?: string) => {
    const wsClient = WebSocketStore.get('onboarding')
    const res = await wsClient.call({
      service: 'notification',
      action: 'fetch-attachments-for',
      payload: { requestId, actionUUID, transitionName },
      withoutTokenRefresh: true,
      isAuth: true,
    })
    return res.data.attachments
  }

  public getUserAccessToRegistrationByEmail = async (
    email: string,
    requestType: RequestTypes
  ): Promise<IUserAccessStatusToRegistration | null> => {
    try {
      const wsClient = WebSocketStore.get('onboarding')
      const res = await wsClient.call<IUserAccessStatusToRegistration>({
        service: 'registration',
        action: 'get-access-status-by-email-and-type',
        payload: { email, requestType },
        withoutTokenRefresh: true,
        isAuth: false,
      })
      return res.data
    } catch (e: any) {
      return null
    }
  }

  public getUserAccessToRegistrationByToken = async (
    token: string,
    requestId: number
  ): Promise<{ hasAccess: boolean; warningMessage: string }> => {
    try {
      const wsClient = WebSocketStore.get('onboarding')
      const response = await wsClient.call<{ hasAccess: boolean; warningMessage: string }>({
        service: 'registration',
        action: 'get-access-status-by-token',
        payload: { token, requestId },
        withoutTokenRefresh: true,
        isAuth: false,
      })
      return response.data
    } catch (e: any) {
      return {
        hasAccess: false,
        warningMessage: e.message || WARNING_MESSAGE.notAccess,
      }
    }
  }

  public checkBPRegistrationRequestExists = async (email: string, excludeRequestId?: number) => {
    try {
      const wsClient = WebSocketStore.get('onboarding')
      const result = await wsClient.call<{
        IsBP: boolean
        RequestId?: number
        MainParam?: string
        Status?: RequestStatus
        Submitted?: number
      }>({
        service: 'bp-registration',
        action: 'check-actual-registration',
        payload: {
          registrationEmail: email,
          excludeRequestId: excludeRequestId,
        },
      })

      if (
        result.status === 'success' &&
        // The server answers `data: null` when it finds nothing; without this the
        // next line dereferences null and the TypeError is swallowed below, which
        // makes a genuine failure indistinguishable from "not found".
        result.data &&
        result.data.RequestId !== undefined &&
        result.data.MainParam !== undefined &&
        result.data.Status !== undefined &&
        result.data.Submitted !== undefined
      ) {
        return {
          IsBP: result.data.IsBP,
          RequestId: result.data.RequestId,
          MainParam: result.data.MainParam,
          Status: result.data.Status,
          Submitted: result.data.Submitted,
        }
      }

      return null
    } catch (e: any) {
      return null
    }
  }

  public getUserAccessToUpdateDistributorsOnBehalfByEmail = async (
    email: string,
    requestId: number
  ): Promise<boolean> => {
    let hasAccess = false
    try {
      const wsClient = WebSocketStore.get('onboarding')
      const response = await wsClient.call<{ access: boolean }>({
        service: 'registration',
        action: 'get-access-to-update-distributors-on-behalf-by-email',
        payload: { email, requestId },
        withoutTokenRefresh: true,
        isAuth: false,
      })
      if (response.status === 'success') {
        hasAccess = response.data.access
      }
    } catch (e: any) {
      Logger.error(e.message)
    }

    return hasAccess
  }

  public checkAccessIfUserCanUpdateCompanyData = async (requestId: number): Promise<boolean> => {
    let hasAccess = false
    try {
      const wsClient = WebSocketStore.get('onboarding')
      const response = await wsClient.call<{ hasAccess: boolean }>({
        service: 'registration',
        action: 'get-access-to-update-company-data',
        payload: { requestId },
      })

      if (response.status === 'success') {
        hasAccess = response.data.hasAccess
      }
    } catch (e: any) {
      Logger.error(e.message)
    }

    return hasAccess
  }
}

export default new OnboardingApi()
